import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { UserService } from './user.service';
import { NotificationService } from './notification.service';
import { LoggedUserReport } from '../../shared/models/user';

@Injectable({
  providedIn: 'root'
})
export class FavoritesService {

  constructor(
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private notificationService: NotificationService
  ) {}

  /**
   * Actualiza el estado de favoritos basado en el usuario logueado
   * @param user Usuario logueado
   * @param report Reporte a verificar
   * @returns boolean indicando si el reporte es favorito
   */
  updateFavoriteStatus(user: any, report: LoggedUserReport): boolean {
    if (user?.favoriteReports) {
      return user.favoriteReports.some((favReport: LoggedUserReport) => favReport.id === report.id);
    }
    return false;
  }

  /**
   * Maneja el guardar/eliminar de favoritos
   * @param report Reporte a agregar/eliminar de favoritos
   * @param isFavorite Estado actual del favorito
   * @param onTogglingStart Callback para manejar el inicio del toggle
   * @param onTogglingEnd Callback para manejar el fin del toggle
   * @returns Observable<boolean> indicando el nuevo estado del favorito
   */
  onSaveFavorite(
    report: LoggedUserReport, 
    isFavorite: boolean, 
    onTogglingStart: () => void,
    onTogglingEnd: () => void
  ): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const loggedUser = this.authenticationService.loggedUser$.value;
      if (!loggedUser?.id && !loggedUser?.userId) {
        this.notificationService.error('Error', 'No se pudo identificar al usuario');
        observer.error('Usuario no identificado');
        return;
      }

      // Iniciar el estado de loading
      onTogglingStart();

      if (isFavorite) {
        this.removeFromFavorites(report).subscribe({
          next: (success) => {
            onTogglingEnd();
            observer.next(!success);
            observer.complete();
          },
          error: (error) => {
            onTogglingEnd();
            observer.error(error);
          }
        });
      } else {
        this.addToFavorites(report).subscribe({
          next: (success) => {
            onTogglingEnd();
            observer.next(success);
            observer.complete();
          },
          error: (error) => {
            onTogglingEnd();
            observer.error(error);
          }
        });
      }
    });
  }

  /**
   * Agrega el reporte a favoritos
   * @param report Reporte a agregar
   * @returns Observable<boolean> indicando si fue exitoso
   */
  private addToFavorites(report: LoggedUserReport): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const id = (this.authenticationService.loggedUser$.value!.userId || this.authenticationService.loggedUser$.value!.id)!;

      this.userService.putUserAccountRelationRef(
        id,
        'FavoriteReports',
        report.id
      ).subscribe({
        next: () => {
          this.authenticationService.toggleFavoriteReport(report.id, true);
          this.notificationService.success('Éxito', 'Reporte agregado a favoritos');
          observer.next(true);
          observer.complete();
        },
        error: (error) => {
          console.error('Error agregando a favoritos:', error);
          this.notificationService.error('Error', 'No se pudo agregar el reporte a favoritos');
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Elimina el reporte de favoritos
   * @param report Reporte a eliminar
   * @returns Observable<boolean> indicando si fue exitoso
   */
  private removeFromFavorites(report: LoggedUserReport): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const id = (this.authenticationService.loggedUser$.value!.userId || this.authenticationService.loggedUser$.value!.id)!;

      this.userService.deleteUserAccountRelationRef(
        id,
        'FavoriteReports',
        report.id
      ).subscribe({
        next: () => {
          this.authenticationService.toggleFavoriteReport(report.id, false);
          this.notificationService.success('Éxito', 'Reporte eliminado de favoritos');
          observer.next(true);
          observer.complete();
        },
        error: (error) => {
          console.error('Error eliminando de favoritos:', error);
          this.notificationService.error('Error', 'No se pudo eliminar el reporte de favoritos');
          observer.next(false);
          observer.complete();
        }
      });
    });
  }
}
